import { ScheduleType } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { Appointment, AppointmentItem } from '@/types/appointments.types';
import { AppointmentMode } from '@/types/enums/appointments.enum';
import { dateFormatter } from '@/utils/formatters/date';
import { addressFormatter } from '@/utils/formatters/format-address';
import { formatDocumentNumber } from '@/utils/formatters/format-document-number';
import { convertHTMLToWhatsAppFormat } from '@/utils/formatters/format-whatsapp';
import { maskPhone } from '@/utils/formatters/phonenumber';
import { professionalDisplayName } from '@/utils/formatters/professional-name';

function bold(text: string): string {
  return `*${text}*`;
}

export function generateWhatsAppText(appointment: Appointment): string {
  const separator = '--------';
  const { referral } = appointment;

  const firstAppointment = appointment.items?.[0] ?? {} as AppointmentItem;

  const getAppointmentDateText = () => {
    const appointmentDate = dateFormatter(appointment.date, 'DD [de] MMMM [de] YYYY').toLowerCase();
    return [`📆 Data: ${bold(appointmentDate)}`];
  };

  const getFlexAppointmentDateText = () => {
    const isFlexScheduled = !!appointment.date;
    return isFlexScheduled ? getAppointmentDateText() : [`${bold('Entre em contato para agendar:')}`];
  };

  const getStandaloneAppointmentDateText = () => {
    return [`${bold('Entre em contato para agendar:')}`];
  };

  const getScheduledTimeText = () => {
    const { start } = firstAppointment;
    if (appointment.scheduleType === ScheduleType.FLEX && appointment.date) {
      return [`⏰ ${bold(dateFormatter(firstAppointment.date, 'time'))}  (Horário agendado)`];
    }
    if (appointment.isTelehealth) {
      return [`⏰ ${bold(dateFormatter(start, 'time'))}  (Horário de Brasília)`];
    }
    return [`⏰ ${bold(dateFormatter(start, 'time'))}  (Horário agendado)`];
  };

  const getOnlineText = () => {
    if (!appointment.professional) {
      return [];
    }
    const professional = professionalDisplayName(appointment.professional);
    return [
      `🖥️ ${bold('Teleconsulta')} - ${professional} iniciará o atendimento próximo ao horário agendado. Pode haver um pequeno atraso devido à agenda médica; pedimos que permaneça disponível e acompanhe o seu celular.`,
    ];
  };

  const getArrivalOrderTimeText = () => {
    const { start, end } = firstAppointment;
    return [`⏰ Por ordem de chegada: ${bold(dateFormatter(start, 'time'))} - ${bold(dateFormatter(end, 'time'))}`];
  };

  const getFlexTimeText = () => {
    if (appointment.scheduleType !== ScheduleType.FLEX) {
      return [];
    }
    if (appointment.date) {
      return getScheduledTimeText();
    }
    const text = [
      appointment.partner.socialName,
      bold(`Whatsapp ${maskPhone(appointment.flexContacts?.whatsappNumber || '')}`),
    ];
    if (appointment.flexContacts?.commercialNumber) {
      text.push('ou');
      text.push(bold(`Telefone ${maskPhone(appointment.flexContacts.commercialNumber)}`));
    }
    return text;
  };

  const getStandaloneTimeText = () => {
    const partnerFacilityPhone = appointment.partner.facility.contacts.phones[0];
    const partnerFacilityWhatsapp = appointment.partner.facility.contacts.whatsapp;

    const text = [appointment.partner.socialName];
    if (partnerFacilityWhatsapp) {
      text.push(bold(`Whatsapp ${maskPhone(partnerFacilityWhatsapp)}`));
    }
    if (partnerFacilityWhatsapp && partnerFacilityPhone) {
      text.push('ou');
    }
    if (partnerFacilityPhone) {
      text.push(bold(`Telefone ${maskPhone(partnerFacilityPhone)}`));
    }
    return text;
  };

  const getObservationsText = () => {
    return [
      `Lembre-se de chegar ${bold('10 minutos antes do atendimento')} e de levar um `,
      `${bold('documento oficial com foto e CPF')}.`,
    ];
  };

  const getPartnerText = () => {
    const { facility, socialName } = appointment.partner;
    const partnerEmail = facility.contacts.email;
    const partnerPhone = facility.contacts.phones.map((phoneNumber) => maskPhone(phoneNumber)).join(', ');
    return [`🏥 ${bold(socialName)}`, `${addressFormatter(facility)}`, `Contato: ${partnerPhone}`, partnerEmail];
  };

  const getProfessionalText = () => {
    if (!appointment.professional) {
      return [];
    }
    const { socialName } = appointment.partner;
    const professional = professionalDisplayName(appointment.professional);
    return [`🏥 ${bold(socialName)}`, `🥼 Profissional: ${bold(professional)}`];
  };

  const getPatientText = () => {
    const { socialName, legalName } = appointment.patient;
    const patientName = socialName && socialName !== legalName ? socialName : legalName;
    return [
      `Agendado para: ${bold(patientName)}`,
      `CPF: ${bold(formatDocumentNumber(appointment.patient.documentNumber))}`,
    ];
  };

  const getInstructionsText = () => {
    return appointment.items.reduce((result: string[], appointmentItem) => {
      const procedureEmoj = appointment.scheduleType === ScheduleType.LABORATORIAL ? '🧪' : '🩺';

      result.push(separator);
      result.push(`${procedureEmoj} ${bold(appointmentItem.procedure.name)}`);

      if (appointment.minAge) {
        result.push(`Restrição etária: ${bold('A partir de')} ${bold(appointment.minAge)} ${bold('anos')}`);
      }
      if (appointment.maxAge) {
        result.push(`Limite máximo de idade: ${bold(appointment.maxAge)} ${bold('anos')}`);
      }
      if (appointment.followUp) {
        result.push(`Aceita retorno em até: ${bold(appointment.followUp)} ${bold('dias')}`);
      }

      if (appointmentItem.procedure.notes) {
        result.push(`Importante: ${bold(appointmentItem.procedure.notes)}`);
      }

      const formattedInstruction = convertHTMLToWhatsAppFormat(appointmentItem.procedure.instructions || '');
      const instructionsByLineBreak = formattedInstruction.split('\n').filter((value) => value);

      result.push('Instruções de preparo:');

      if (instructionsByLineBreak?.length) {
        instructionsByLineBreak.forEach((part) => {
          result.push(part);
        });
      } else {
        result.push('Sem instruções de preparo');
      }

      return result;
    }, []);
  };

  let scheduledText: string[] = [];

  if (appointment.isTelehealth) {
    scheduledText = [
      `${bold('GUIA DE AGENDAMENTO MEDPREV')}`,
      separator,
      ...getAppointmentDateText(),
      ...getScheduledTimeText(),
      ...getOnlineText(),
      `Localizador: ${bold(referral)}`,
      separator,
      ...getPatientText(),
      separator,
      ...getProfessionalText(),
      ...getInstructionsText(),
    ];
  } else {
    scheduledText = [
      `${bold('GUIA DE AGENDAMENTO MEDPREV')}`,
      separator,
      ...getAppointmentDateText(),
      ...getScheduledTimeText(),
      `Localizador: ${bold(referral)}`,
      ...getObservationsText(),
      separator,
      ...getPatientText(),
      separator,
      ...getPartnerText(),
      ...getInstructionsText(),
    ];
  }

  const arrivalOrderText = [
    `${bold('GUIA DE AGENDAMENTO MEDPREV')}`,
    separator,
    ...getAppointmentDateText(),
    ...getArrivalOrderTimeText(),
    `Localizador: ${bold(referral)}`,
    ...getObservationsText(),
    separator,
    ...getPatientText(),
    separator,
    ...getPartnerText(),
    ...getInstructionsText(),
  ];

  const laboratorialText = [
    `${bold('GUIA DE AGENDAMENTO MEDPREV')}`,
    'Exames de laboratório',
    separator,
    ...getAppointmentDateText(),
    ...getArrivalOrderTimeText(),
    `Localizador: ${bold(referral)}`,
    ...getObservationsText(),
    separator,
    ...getPatientText(),
    separator,
    ...getPartnerText(),
    'Exames:',
    ...getInstructionsText(),
  ];

  const flexText = [
    `${bold('ENCAIXE MEDPREV')}`,
    firstAppointment.procedure.name,
    separator,
    ...getFlexAppointmentDateText(),
    ...getFlexTimeText(),
    '',
    `Informe o código: ${bold(referral)}`,
    separator,
    ...getPatientText(),
    separator,
    ...getPartnerText(),
    ...getInstructionsText(),
  ];

  const standaloneText = [
    `${bold('GUIA AVULSA MEDPREV')}`,
    firstAppointment.procedure.name,
    separator,
    ...getStandaloneAppointmentDateText(),
    ...getStandaloneTimeText(),
    '',
    `Informe o código: ${bold(referral)}`,
    separator,
    ...getPatientText(),
    separator,
    ...getPartnerText(),
    ...getInstructionsText(),
  ];

  const texts = {
    [ScheduleType.SCHEDULED]: scheduledText,
    [ScheduleType.ARRIVAL_ORDER]: arrivalOrderText,
    [ScheduleType.LABORATORIAL]: laboratorialText,
    [ScheduleType.FLEX]: flexText,
  };

  if (appointment.mode === AppointmentMode.STANDALONE) {
    return `${standaloneText.join('\n')}\n\n`;
  }

  if (appointment.scheduleType === ScheduleType.SCHEDULED) {
    return `${texts[ScheduleType.SCHEDULED].join('\n')}\n\n`;
  }

  if (appointment.scheduleType === ScheduleType.ARRIVAL_ORDER) {
    return `${texts[ScheduleType.ARRIVAL_ORDER].join('\n')}\n\n`;
  }

  if (appointment.scheduleType === ScheduleType.LABORATORIAL) {
    return `${texts[ScheduleType.LABORATORIAL].join('\n')}\n\n`;
  }

  if (appointment.scheduleType === ScheduleType.FLEX) {
    return `${texts[ScheduleType.FLEX].join('\n')}\n\n`;
  }

  return '';
}
